import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  EXERCISE_PAGE_SIZE,
  getExercises,
  getMyExercises,
  getSimilarExercises,
  type Exercise,
} from "../lib/api";
import { EQUIPMENT_FILTERS, MUSCLE_GROUPS, equipmentLabel, muscleLabel } from "../lib/format";
import { splitHighlight } from "../lib/highlight";
import { haptic } from "../lib/telegram";
import DotLoader from "./DotLoader";
import ExerciseForm from "./ExerciseForm";
import ExerciseIcon from "./ExerciseIcon";

export interface ExerciseSection {
  title: string;
  items: Exercise[];
}

interface Props {
  onPick: (exercise: Exercise) => void;
  /** Разрешить «+ Своё упражнение»: форма с характеристиками, созданное уходит в onPick. */
  allowCreate?: boolean;
  /** Что показать до поиска, кроме «Похожих» и «Недавних»: например, альтернативы. */
  sections?: ExerciseSection[];
  /** Для замены: сверху «Похожие» на это упражнение. */
  similarTo?: number;
  /** «Недавние» до поиска. */
  showMine?: boolean;
  /** Библиотека: до поиска ещё и весь список по алфавиту. */
  browse?: boolean;
  exclude?: number[];
  /** Что сказать, если всё найденное исключено. */
  excludedHint?: string;
  placeholder?: string;
  /** С чего начать поиск: на проверке импорта — с названия из файла. */
  initialQuery?: string;
}

// «Недавние» живут минуту: шторку открывают по нескольку раз за тренировку, и
// ждать сеть на каждое открытие незачем.
const MINE_TTL_MS = 60_000;
let mineCache: { at: number; promise: Promise<Exercise[]> } | null = null;

function loadMine(): Promise<Exercise[]> {
  if (!mineCache || Date.now() - mineCache.at > MINE_TTL_MS) {
    mineCache = {
      at: Date.now(),
      promise: getMyExercises().catch(() => {
        mineCache = null;
        return [];
      }),
    };
  }
  return mineCache.promise;
}

const MUSCLES = MUSCLE_GROUPS.filter((g) => g.key !== "other");

/**
 * Выбор упражнения — один на все места (07-exercise-search-plan.md, шаг 2):
 * шторка тренировки, конструктор программ, библиотека, редактор.
 *
 * До поиска — то, что человек скорее всего ищет: похожие, недавние, из
 * программы; нужное берётся тапом, без клавиатуры. Поиск и фильтры ранжирует
 * сервер и присылает подсветку совпадений.
 */
export default function ExerciseSearch({
  onPick,
  allowCreate = false,
  sections = [],
  similarTo,
  showMine = true,
  browse = false,
  exclude = [],
  excludedHint = "Найденное уже выбрано.",
  placeholder = "Название, мышца или снаряд",
  initialQuery = "",
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [muscle, setMuscle] = useState<string | null>(null);
  const [equipment, setEquipment] = useState<string | null>(null);
  const [results, setResults] = useState<Exercise[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [mine, setMine] = useState<Exercise[]>([]);
  const [similar, setSimilar] = useState<Exercise[]>([]);
  const [all, setAll] = useState<Exercise[] | null>(null);
  // Название, с которым открыта форма своего упражнения; null — формы нет.
  const [composing, setComposing] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const chipRow = useRef<HTMLDivElement>(null);

  const q = query.trim();
  const active = q.length >= 2 || muscle !== null || equipment !== null;
  const excludeKey = exclude.join(",");
  const hidden = useMemo(() => new Set(exclude), [excludeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showMine) return;
    let live = true;
    void loadMine().then((items) => live && setMine(items));
    return () => {
      live = false;
    };
  }, [showMine]);

  useEffect(() => {
    if (similarTo == null) return;
    let live = true;
    getSimilarExercises(similarTo)
      .then((items) => live && setSimilar(items))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [similarTo]);

  useEffect(() => {
    if (!browse) return;
    let live = true;
    getExercises()
      .then((items) => live && setAll(items))
      .catch(() => live && setAll([]));
    return () => {
      live = false;
    };
  }, [browse]);

  useEffect(() => {
    if (!active) {
      setResults(null);
      setSearching(false);
      return;
    }
    let live = true;
    setSearching(true);
    // Прежняя выдача остаётся на месте, пока не пришла новая: без мигания пустотой.
    const timer = setTimeout(() => {
      getExercises(q.length >= 2 ? q : undefined, muscle ?? undefined, equipment ?? undefined)
        .then((items) => live && setResults(items))
        .catch(() => live && setResults([]))
        .finally(() => live && setSearching(false));
    }, 180);
    return () => {
      // Ответ на устаревший запрос не должен перезаписать свежий.
      live = false;
      clearTimeout(timer);
    };
  }, [q, muscle, equipment, active]);

  const idle = useMemo(() => {
    const seen = new Set(hidden);
    const out: ExerciseSection[] = [];
    const add = (title: string, items: Exercise[]) => {
      const fresh = items.filter((e) => !seen.has(e.id));
      fresh.forEach((e) => seen.add(e.id));
      if (fresh.length) out.push({ title, items: fresh });
    };
    add("Похожие", similar);
    sections.forEach((s) => add(s.title, s.items));
    add("Недавние", mine);
    return out;
  }, [hidden, similar, sections, mine]);

  const visible = (results ?? []).filter((e) => !hidden.has(e.id));

  const choose = (exercise: Exercise) => {
    haptic("medium");
    input.current?.blur();
    onPick(exercise);
  };

  const toggle = (current: string | null, set: (v: string | null) => void, value: string) => {
    haptic();
    set(current === value ? null : value);
  };

  // Включённый фильтр встаёт в начало ленты — показываем начало. После
  // перестановки, а не в обработчике: браузер успевает докрутить к нажатому
  // чипу раньше, и лента оставалась сдвинутой.
  useLayoutEffect(() => {
    if (chipRow.current) chipRow.current.scrollLeft = 0;
  }, [muscle, equipment]);

  const row = (exercise: Exercise) => (
    <button key={exercise.id} className="picker__row" onClick={() => choose(exercise)}>
      <ExerciseIcon exercise={exercise} />
      <span className="picker__text">
        <span className="picker__name">
          {splitHighlight(exercise.name_ru, exercise.highlight).map((piece, i) =>
            piece.hit ? (
              <mark key={i} className="hl">
                {piece.text}
              </mark>
            ) : (
              <span key={i}>{piece.text}</span>
            ),
          )}
        </span>
        <span className="picker__meta">
          {muscleLabel(exercise.primary_muscle)} · {equipmentLabel(exercise.equipment)}
        </span>
      </span>
    </button>
  );

  // Включённые фильтры — в начале ленты: иначе выбранный снаряд уезжает за край,
  // и непонятно, почему выдача такая узкая.
  const chips = [
    ...MUSCLES.map((g) => ({ ...g, kind: "muscle" as const, on: muscle === g.key })),
    ...EQUIPMENT_FILTERS.map((g) => ({ ...g, kind: "equipment" as const, on: equipment === g.key })),
  ].sort((a, b) => Number(b.on) - Number(a.on));

  if (composing !== null) {
    // Форма на месте выдачи, а не второй шторкой поверх первой: вложенные
    // шторки на телефоне путают, какая из них закроется жестом.
    const done = (exercise: Exercise) => {
      setComposing(null);
      onPick(exercise);
    };
    return (
      <div className="search">
        <ExerciseForm
          name={composing}
          onSaved={done}
          onPickExisting={done}
          onCancel={() => setComposing(null)}
        />
      </div>
    );
  }

  return (
    <div
      className="search"
      // Палец повёл список — клавиатура больше не нужна и закрывает полэкрана.
      onTouchMove={() => {
        if (document.activeElement === input.current) input.current?.blur();
      }}
    >
      <div className="search__head">
        <div className="search__field">
          <input
            ref={input}
            className="input search__input"
            type="search"
            enterKeyHint="search"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Enter берёт первое найденное: в зале набрали «присед» — и готово.
              if (e.key === "Enter" && active && visible[0]) choose(visible[0]);
            }}
          />
          {searching && (
            <span className="search__busy">
              <DotLoader />
            </span>
          )}
          {!searching && query && (
            <button
              className="search__clear"
              aria-label="Очистить"
              onClick={() => {
                setQuery("");
                input.current?.focus();
              }}
            >
              ×
            </button>
          )}
        </div>
        <div ref={chipRow} className="chips search__chips" aria-label="Фильтры">
          {chips.map((chip) => (
            <button
              key={`${chip.kind}:${chip.key}`}
              aria-pressed={chip.on}
              className={`chip ${chip.on ? "chip--active" : ""} ${chip.kind === "equipment" ? "chip--equipment" : ""}`}
              onClick={() =>
                chip.kind === "muscle"
                  ? toggle(muscle, setMuscle, chip.key)
                  : toggle(equipment, setEquipment, chip.key)
              }
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {active ? (
        <>
          {visible.length > 0 && <div className="picker">{visible.map(row)}</div>}
          {results && !searching && visible.length === 0 && (
            <p className="search__empty">
              {results.length > 0 ? excludedHint : "Ничего не нашлось."}
            </p>
          )}
          {results && results.length >= EXERCISE_PAGE_SIZE && (
            <p className="search__empty">
              Показаны первые {EXERCISE_PAGE_SIZE}. Уточните поиск или фильтр.
            </p>
          )}
        </>
      ) : (
        <>
          {idle.map((section) => (
            <section key={section.title} className="search__section">
              <p className="card__title">{section.title}</p>
              <div className="picker">{section.items.map(row)}</div>
            </section>
          ))}
          {browse && all && (
            <section className="search__section">
              <p className="card__title">Все упражнения</p>
              <div className="picker">{all.filter((e) => !hidden.has(e.id)).map(row)}</div>
            </section>
          )}
          {!browse && idle.length === 0 && (
            <p className="search__empty">Начните вводить название или выберите фильтр.</p>
          )}
        </>
      )}

      {allowCreate && !searching && (
        <button
          className="wo-btn search__create"
          onClick={() => {
            haptic();
            input.current?.blur();
            setComposing(q);
          }}
        >
          {q.length >= 2 ? `+ Своё упражнение «${q}»` : "+ Своё упражнение"}
        </button>
      )}
    </div>
  );
}
